import React, { useState, useEffect, useRef, useCallback } from "react";
import { Redirect } from "react-router-dom";
import { toast } from "react-toastify";
import {
  Loader2,
  Save,
  TrendingUp,
  AlertTriangle,
  RefreshCw,
  Plus,
  Trash2,
  Wallet,
  MessageSquare,
  Info,
} from "lucide-react";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { amountToCents, centsToAmount } from "../../helpers/money";

const inputClass =
  "w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all";
const labelClass =
  "text-xs font-medium text-slate-600 dark:text-slate-400 mb-1.5 block";
const cardClass =
  "rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5";

const CATEGORY_LABELS = {
  service: "Servicio (mensajes regulares)",
  utility: "Utilidad",
  marketing: "Marketing",
  authentication: "Autenticación",
  referral_conversion: "Conversión por referido",
};

/** Formatea centavos en una moneda arbitraria (Meta cobra en USD, el cobro puede ser en otra). */
const money = (cents, currency) =>
  new Intl.NumberFormat("es-DO", {
    style: "currency",
    currency: currency || "USD",
  }).format((Number(cents) || 0) / 100);

const monthInputValue = (date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;

/** "2026-07" -> rango [primer día 00:00, último día 23:59:59.999] en hora local. */
const monthRange = (value) => {
  const [year, month] = value.split("-").map(Number);
  return {
    start: new Date(year, month - 1, 1, 0, 0, 0, 0).toISOString(),
    end: new Date(year, month, 0, 23, 59, 59, 999).toISOString(),
  };
};

const StatCard = ({ label, value, hint, tone = "default" }) => {
  const toneClass =
    tone === "positive"
      ? "text-emerald-600 dark:text-emerald-400"
      : tone === "negative"
      ? "text-rose-600 dark:text-rose-400"
      : "text-slate-900 dark:text-slate-100";

  return (
    <div className={cardClass}>
      <p className="text-xs font-medium text-slate-500 dark:text-slate-400">
        {label}
      </p>
      <p className={`text-2xl font-bold mt-1 tracking-tight ${toneClass}`}>
        {value}
      </p>
      {hint && (
        <p className="text-[11px] text-slate-400 dark:text-slate-500 mt-1">
          {hint}
        </p>
      )}
    </div>
  );
};

const BreakdownTable = ({ title, rows, currency, keyLabel }) => (
  <div className={cardClass}>
    <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-100 mb-3">
      {title}
    </h2>
    {rows.length === 0 ? (
      <p className="text-sm text-slate-400 dark:text-slate-500">
        Sin datos en el período.
      </p>
    ) : (
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wide text-slate-400 dark:text-slate-500">
              <th className="pb-2 pr-3 font-medium">{keyLabel}</th>
              <th className="pb-2 px-3 font-medium text-right">Mensajes</th>
              <th className="pb-2 px-3 font-medium text-right">Cobrados</th>
              <th className="pb-2 pl-3 font-medium text-right">Gasto</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
            {rows.map((row) => (
              <tr key={row.key}>
                <td className="py-2 pr-3 text-slate-700 dark:text-slate-300">
                  {row.label}
                </td>
                <td className="py-2 px-3 text-right text-slate-500 dark:text-slate-400 tabular-nums">
                  {row.messages}
                </td>
                <td className="py-2 px-3 text-right text-slate-500 dark:text-slate-400 tabular-nums">
                  {row.chargeable}
                </td>
                <td className="py-2 pl-3 text-right font-medium text-slate-900 dark:text-slate-100 tabular-nums">
                  {money(row.costCents, currency)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </div>
);

const Costs = () => {
  const isMounted = useRef(true);
  const [allowed, setAllowed] = useState(null);
  const [month, setMonth] = useState(monthInputValue(new Date()));
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(false);

  const [pricing, setPricing] = useState(null);
  const [savingPricing, setSavingPricing] = useState(false);

  const [rates, setRates] = useState([]);
  const [rateMeta, setRateMeta] = useState({
    metaRateCurrency: "USD",
    metaFxRateMilli: 0,
    categories: [],
  });
  const [savingRates, setSavingRates] = useState(false);

  const [analytics, setAnalytics] = useState(null);
  const [loadingAnalytics, setLoadingAnalytics] = useState(false);

  useEffect(() => () => { isMounted.current = false; }, []);

  const loadOverview = useCallback(async (targetMonth) => {
    setLoading(true);
    try {
      const { data } = await api.get("/costs/overview", {
        params: monthRange(targetMonth),
      });
      if (!isMounted.current) return;
      setOverview(data);
      setPricing({
        baseAmountCents: data.charged.baseCents,
        aiAmountCents: data.charged.aiCents,
        aiEnabled: data.charged.aiEnabled,
      });
    } catch (err) {
      if (isMounted.current) toastError(err);
    } finally {
      if (isMounted.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      try {
        const { data: status } = await api.get("/billing/status");
        if (!isMounted.current) return;
        if (!status.isOwner) {
          setAllowed(false);
          return;
        }
        setAllowed(true);

        const { data: ratesData } = await api.get("/costs/rates");
        if (!isMounted.current) return;
        setRates(ratesData.rates || []);
        setRateMeta({
          metaRateCurrency: ratesData.metaRateCurrency,
          metaFxRateMilli: ratesData.metaFxRateMilli,
          categories: ratesData.categories || [],
        });
      } catch (err) {
        if (isMounted.current) setAllowed(false);
      }
    };
    init();
  }, []);

  useEffect(() => {
    if (allowed) loadOverview(month);
  }, [allowed, month, loadOverview]);

  const handleSavePricing = async () => {
    setSavingPricing(true);
    try {
      await api.put("/costs/pricing", {
        baseAmountCents: Number(pricing.baseAmountCents),
        aiAmountCents: Number(pricing.aiAmountCents),
        aiEnabled: !!pricing.aiEnabled,
      });
      toast.success("Precios guardados");
      await loadOverview(month);
    } catch (err) {
      toastError(err);
    } finally {
      if (isMounted.current) setSavingPricing(false);
    }
  };

  const handleSaveRates = async () => {
    setSavingRates(true);
    try {
      const { data } = await api.put("/costs/rates", {
        rates: rates.map((r) => ({
          prefix: String(r.prefix || "*").trim(),
          category: r.category,
          rateCents: Number(r.rateCents) || 0,
        })),
        metaRateCurrency: rateMeta.metaRateCurrency,
        metaFxRateMilli: Number(rateMeta.metaFxRateMilli) || 0,
      });
      setRates(data.rates || []);
      toast.success("Tarifas guardadas");
      // El gasto se recalcula con las tarifas nuevas de acá en adelante; el
      // histórico conserva la tarifa que estaba vigente al enviarse.
      await loadOverview(month);
    } catch (err) {
      toastError(err);
    } finally {
      if (isMounted.current) setSavingRates(false);
    }
  };

  const handleLoadAnalytics = async () => {
    setLoadingAnalytics(true);
    try {
      const { data } = await api.get("/costs/meta-analytics", {
        params: monthRange(month),
      });
      if (isMounted.current) setAnalytics(data);
    } catch (err) {
      if (isMounted.current) toastError(err);
    } finally {
      if (isMounted.current) setLoadingAnalytics(false);
    }
  };

  const patchRate = (index, changes) =>
    setRates((prev) =>
      prev.map((r, i) => (i === index ? { ...r, ...changes } : r))
    );

  const addRate = () =>
    setRates((prev) => [
      ...prev,
      { prefix: "*", category: "service", rateCents: 0 },
    ]);

  const removeRate = (index) =>
    setRates((prev) => prev.filter((_, i) => i !== index));

  if (allowed === false) return <Redirect to="/" />;

  if (allowed === null) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  const metaCurrency = rateMeta.metaRateCurrency;
  const billingCurrency = overview?.currency || "DOP";
  const totals = overview?.metaCost?.totals;
  const margin = overview?.margin;

  return (
    <div className="p-3 sm:p-6 max-w-5xl mx-auto space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100 tracking-tight">
            Costos y márgenes
          </h1>
          <p className="text-slate-500 dark:text-slate-400 text-sm mt-1">
            Lo que cobrás, lo que te cuesta la mensajería de WhatsApp y la
            diferencia. Sólo visible para el propietario.
          </p>
        </div>
        <div className="flex items-end gap-2">
          <div>
            <label className={labelClass}>Período</label>
            <input
              type="month"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              className={inputClass}
            />
          </div>
          <button
            type="button"
            onClick={() => loadOverview(month)}
            disabled={loading}
            className="px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 text-sm text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-50 flex items-center gap-2"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4" />
            )}
            Actualizar
          </button>
        </div>
      </div>

      {/* Resumen */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatCard
          label="Cobrás al cliente"
          value={money(overview?.charged?.totalCents || 0, billingCurrency)}
          hint={
            overview?.charged?.billingEnabled
              ? "Mensual, sin impuestos"
              : "Facturación desactivada"
          }
        />
        <StatCard
          label="Gasto WhatsApp API"
          value={money(totals?.costCents || 0, metaCurrency)}
          hint={`${totals?.chargeable || 0} cobrables de ${
            totals?.messages || 0
          } mensajes`}
        />
        <StatCard
          label="Margen"
          value={
            margin?.amountCents === null || margin?.amountCents === undefined
              ? "—"
              : money(margin.amountCents, billingCurrency)
          }
          hint={margin?.blockedReason || "Cobro menos gasto de mensajería"}
          tone={
            margin?.amountCents === null || margin?.amountCents === undefined
              ? "default"
              : margin.amountCents >= 0
              ? "positive"
              : "negative"
          }
        />
      </div>

      {margin?.blockedReason && (
        <div className="rounded-xl border border-amber-200 dark:border-amber-900 bg-amber-50 dark:bg-amber-950/30 p-4 flex gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-500 shrink-0 mt-0.5" />
          <div className="text-sm text-amber-800 dark:text-amber-200">
            <p className="font-medium">No se puede calcular el margen</p>
            <p className="mt-0.5">
              Las tarifas de Meta están en {metaCurrency} y cobrás en{" "}
              {billingCurrency}. Cargá el tipo de cambio más abajo para poder
              restar una cosa de la otra.
            </p>
          </div>
        </div>
      )}

      {totals?.unpricedChargeable > 0 && (
        <div className="rounded-xl border border-amber-200 dark:border-amber-900 bg-amber-50 dark:bg-amber-950/30 p-4 flex gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-500 shrink-0 mt-0.5" />
          <div className="text-sm text-amber-800 dark:text-amber-200">
            <p className="font-medium">
              {totals.unpricedChargeable} mensaje(s) facturable(s) sin tarifa
            </p>
            <p className="mt-0.5">
              Meta los cobra pero no hay tarifa cargada para su categoría o
              país, así que cuentan como 0 y el gasto real es mayor que el que
              ves.
            </p>
          </div>
        </div>
      )}

      {/* Precios que cobro */}
      <div className={`${cardClass} space-y-4`}>
        <div className="flex items-center gap-2">
          <Wallet className="w-4 h-4 text-slate-400" />
          <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
            Lo que cobrás por la plataforma
          </h2>
        </div>
        <p className="text-xs text-slate-500 dark:text-slate-400">
          Es el mismo precio que usa la facturación: cambiarlo acá cambia lo que
          se le factura al cliente.
        </p>

        {pricing && (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={labelClass}>
                  Plataforma / mes ({billingCurrency})
                </label>
                <input
                  type="number"
                  min={0}
                  step="0.01"
                  value={centsToAmount(pricing.baseAmountCents)}
                  onChange={(e) =>
                    setPricing((prev) => ({
                      ...prev,
                      baseAmountCents: amountToCents(e.target.value),
                    }))
                  }
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass}>
                  Inteligencia artificial / mes ({billingCurrency})
                </label>
                <input
                  type="number"
                  min={0}
                  step="0.01"
                  disabled={!pricing.aiEnabled}
                  value={centsToAmount(pricing.aiAmountCents)}
                  onChange={(e) =>
                    setPricing((prev) => ({
                      ...prev,
                      aiAmountCents: amountToCents(e.target.value),
                    }))
                  }
                  className={`${inputClass} disabled:opacity-50`}
                />
                <label className="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-400 mt-2">
                  <input
                    type="checkbox"
                    checked={!!pricing.aiEnabled}
                    onChange={(e) =>
                      setPricing((prev) => ({
                        ...prev,
                        aiEnabled: e.target.checked,
                      }))
                    }
                    className="rounded border-slate-300"
                  />
                  Cobrar la IA
                </label>
              </div>
            </div>

            <div className="flex justify-end">
              <button
                type="button"
                onClick={handleSavePricing}
                disabled={savingPricing}
                className="px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium disabled:opacity-50 flex items-center gap-2"
              >
                {savingPricing ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                Guardar precios
              </button>
            </div>
          </>
        )}
      </div>

      {/* Desgloses del gasto */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <BreakdownTable
          title="Gasto por categoría"
          rows={overview?.metaCost?.byCategory || []}
          currency={metaCurrency}
          keyLabel="Categoría"
        />
        <BreakdownTable
          title="Gasto por plantilla"
          rows={overview?.metaCost?.byTemplate || []}
          currency={metaCurrency}
          keyLabel="Plantilla"
        />
        <BreakdownTable
          title="Gasto por tipo de conexión"
          rows={overview?.metaCost?.byChannel || []}
          currency={metaCurrency}
          keyLabel="Tipo"
        />
        <BreakdownTable
          title="Gasto por conexión"
          rows={overview?.metaCost?.byConnection || []}
          currency={metaCurrency}
          keyLabel="Conexión"
        />
        <BreakdownTable
          title="Gasto por prefijo de país"
          rows={overview?.metaCost?.byCountry || []}
          currency={metaCurrency}
          keyLabel="Prefijo"
        />
      </div>

      {/* Tarifas de Meta */}
      <div className={`${cardClass} space-y-4`}>
        <div className="flex items-center gap-2">
          <MessageSquare className="w-4 h-4 text-slate-400" />
          <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
            Tarifas de Meta
          </h2>
        </div>
        <p className="text-xs text-slate-500 dark:text-slate-400">
          Lo que Meta te cobra por mensaje según categoría y país. Con el nuevo
          modelo de precios de Meta, todos los mensajes salientes de WhatsApp
          Cloud y Coexistencia (texto y multimedia) se cobran como mensajes de
          servicio (<code className="font-mono">service</code>) en cobro por
          mensaje (PMP), incluso dentro de las 24 horas. El prefijo es el del
          número del destinatario (<code className="font-mono">1809</code>
          , <code className="font-mono">52</code>) y gana el más largo que
          coincida; <code className="font-mono">*</code> es el comodín para todo
          lo demás. Los envíos ya registrados conservan la tarifa que estaba
          vigente en su momento.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className={labelClass}>Moneda de las tarifas</label>
            <input
              type="text"
              maxLength={3}
              value={rateMeta.metaRateCurrency}
              onChange={(e) =>
                setRateMeta((prev) => ({
                  ...prev,
                  metaRateCurrency: e.target.value.toUpperCase(),
                }))
              }
              className={inputClass}
            />
            <p className="text-[11px] text-slate-400 mt-1">
              Meta factura normalmente en USD.
            </p>
          </div>
          <div>
            <label className={labelClass}>
              Tipo de cambio ({billingCurrency} por 1 {metaCurrency})
            </label>
            <input
              type="number"
              min={0}
              step="0.001"
              value={(Number(rateMeta.metaFxRateMilli) || 0) / 1000}
              onChange={(e) =>
                setRateMeta((prev) => ({
                  ...prev,
                  metaFxRateMilli: Math.round(
                    (Number(e.target.value) || 0) * 1000
                  ),
                }))
              }
              className={inputClass}
            />
            <p className="text-[11px] text-slate-400 mt-1">
              Necesario para el margen. 0 = sin configurar.
            </p>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-slate-400 dark:text-slate-500">
                <th className="pb-2 pr-3 font-medium">Prefijo</th>
                <th className="pb-2 px-3 font-medium">Categoría</th>
                <th className="pb-2 px-3 font-medium">
                  Tarifa ({metaCurrency})
                </th>
                <th className="pb-2 pl-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {rates.map((rate, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <tr key={`${rate.id || "new"}-${index}`}>
                  <td className="py-2 pr-3">
                    <input
                      type="text"
                      value={rate.prefix}
                      onChange={(e) =>
                        patchRate(index, { prefix: e.target.value })
                      }
                      className={`${inputClass} max-w-[7rem]`}
                    />
                  </td>
                  <td className="py-2 px-3">
                    <select
                      value={rate.category}
                      onChange={(e) =>
                        patchRate(index, { category: e.target.value })
                      }
                      className={inputClass}
                    >
                      {(rateMeta.categories.length
                        ? rateMeta.categories
                        : Object.keys(CATEGORY_LABELS)
                      ).map((c) => (
                        <option key={c} value={c}>
                          {CATEGORY_LABELS[c] || c}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="py-2 px-3">
                    <input
                      type="number"
                      min={0}
                      step="0.0001"
                      value={centsToAmount(rate.rateCents)}
                      onChange={(e) =>
                        patchRate(index, {
                          rateCents: amountToCents(e.target.value),
                        })
                      }
                      className={`${inputClass} max-w-[9rem]`}
                    />
                  </td>
                  <td className="py-2 pl-3 text-right">
                    <button
                      type="button"
                      onClick={() => removeRate(index)}
                      className="p-2 rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/30"
                      title="Quitar tarifa"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex justify-between">
          <button
            type="button"
            onClick={addRate}
            className="px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 text-sm text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Agregar tarifa
          </button>
          <button
            type="button"
            onClick={handleSaveRates}
            disabled={savingRates}
            className="px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium disabled:opacity-50 flex items-center gap-2"
          >
            {savingRates ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            Guardar tarifas
          </button>
        </div>
      </div>

      {/* Contraste con lo que reporta Meta */}
      <div className={`${cardClass} space-y-4`}>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-slate-400" />
            <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
              Lo que reporta Meta
            </h2>
          </div>
          <button
            type="button"
            onClick={handleLoadAnalytics}
            disabled={loadingAnalytics}
            className="px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 text-sm text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-50 flex items-center gap-2"
          >
            {loadingAnalytics ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4" />
            )}
            Consultar a Meta
          </button>
        </div>

        <div className="flex gap-2 text-xs text-slate-500 dark:text-slate-400">
          <Info className="w-4 h-4 shrink-0 mt-px" />
          <p>
            Consulta en vivo a la analítica de conversaciones del WABA. Sirve
            para contrastar con la estimación de arriba: si difieren, revisá las
            tarifas. Los importes vienen en la moneda de la cuenta de Meta y
            cuentan conversaciones, no mensajes.
          </p>
        </div>

        {analytics === null ? (
          <p className="text-sm text-slate-400 dark:text-slate-500">
            Sin consultar todavía.
          </p>
        ) : analytics.length === 0 ? (
          <p className="text-sm text-slate-400 dark:text-slate-500">
            No hay conexiones de WhatsApp Cloud API o coexistencia.
          </p>
        ) : (
          <div className="space-y-2">
            {analytics.map((row) => (
              <div
                key={row.whatsappId}
                className="rounded-lg border border-slate-100 dark:border-slate-800 p-3"
              >
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <p className="text-sm font-medium text-slate-800 dark:text-slate-200">
                    {row.connectionName}
                  </p>
                  {row.ok ? (
                    <p className="text-sm tabular-nums text-slate-900 dark:text-slate-100">
                      {row.conversations} conversaciones ·{" "}
                      <span className="font-semibold">
                        {(Number(row.cost) || 0).toFixed(2)}
                      </span>{" "}
                      <span className="text-xs text-slate-400">
                        (moneda de la cuenta de Meta)
                      </span>
                    </p>
                  ) : (
                    <p className="text-xs text-amber-700 dark:text-amber-400 max-w-md text-right">
                      {row.error}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Costs;
